import * as React from 'react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/** One option in a segmented control. */
export interface SegmentOption<T extends string = string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
  color?: string;
}

/** Exclusive option pills with optional size and view-toggle preset. */
interface SegmentedControlProps<T extends string = string> {
  value: string;
  onChange: (value: T) => void;
  options: readonly SegmentOption<T>[];
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  preset?: 'view-toggle';
}

/** Container height classes for sm/md/lg segmented controls. */
const CONTAINER_HEIGHT_CLASSES = {
  sm: 'h-9',
  md: 'h-10',
  lg: 'h-12',
} as const;

/** Segment button padding and type size per control size. */
const BUTTON_SIZE_CLASSES = {
  sm: 'px-3 text-xs',
  md: 'px-4 text-sm',
  lg: 'px-6 text-base',
} as const;

/** Exclusive button group (optional view-toggle preset). */
export const SegmentedControl = <T extends string = string>({
  value,
  onChange,
  options,
  disabled = false,
  className,
  size = 'md',
  preset,
}: SegmentedControlProps<T>) => {
  function handleOptionClick(optionValue: T): void {
    if (!disabled) onChange(optionValue);
  }

  function renderViewToggleOption(option: SegmentOption<T>): React.ReactNode {
    const isActive = value === option.value;
    function handleClick(): void {
      handleOptionClick(option.value);
    }
    return (
      <Tooltip key={option.value}>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn('view-toggle-button', isActive && 'view-toggle-button-active')}
            onClick={handleClick}
            disabled={disabled}
            aria-label={option.label}
          >
            {option.icon}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{option.label}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  function renderDefaultOption(option: SegmentOption<T>, index: number): React.ReactNode {
    const isActive = value === option.value;
    const isLast = index === options.length - 1;
    const color = option.color;
    let buttonStyle: React.CSSProperties | undefined;
    if (color) {
      buttonStyle = { '--seg-color': color } as React.CSSProperties;
    }
    let activeClass: string;
    if (isActive) {
      activeClass = 'bg-[var(--color-2-nuance-1)] text-white';
    } else {
      activeClass =
        'bg-transparent text-[var(--color-2-nuance-1)] hover:bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)]';
    }
    function handleClick(): void {
      handleOptionClick(option.value);
    }
    return (
      <button
        key={option.value}
        type="button"
        onClick={handleClick}
        disabled={disabled}
        style={buttonStyle}
        className={cn(
          BUTTON_SIZE_CLASSES[size],
          'flex-1 inline-flex items-center justify-center gap-1.5 whitespace-nowrap',
          'font-medium transition-all duration-200 cursor-pointer',
          !isLast && 'border-r border-[var(--color-2-nuance-1)]',
          activeClass,
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        {option.icon ? (
          <span className="shrink-0 inline-flex" style={!isActive && color ? { color } : undefined}>
            {option.icon}
          </span>
        ) : null}
        {option.label}
      </button>
    );
  }

  if (preset === 'view-toggle') {
    const viewToggleButtons = options.map((option) => renderViewToggleOption(option));
    return (
      <TooltipProvider delayDuration={200}>
        <div className={cn('view-toggle-container', className)}>{viewToggleButtons}</div>
      </TooltipProvider>
    );
  }

  const defaultButtons = options.map((option, index) => renderDefaultOption(option, index));
  return (
    <div
      className={cn(
        'flex rounded-lg overflow-hidden',
        'border border-[var(--color-2-nuance-1)]',
        CONTAINER_HEIGHT_CLASSES[size],
        className,
      )}
    >
      {defaultButtons}
    </div>
  );
};
